import { useContext, useEffect, useState } from 'react';
import { Link, useNavigate, useOutletContext } from 'react-router-dom';
import Checkbox from '../../common/Checkbox/Checkbox';
import { ButtonBack } from '../../common/Button/ButtonBack';
import { renderRadioOptions, setCountryCode } from '../../common/HelperMethods';
import {
  PageAbsolutePath,
  InsuranceLoadingMessage
} from '../../../consts/Common';
import { SiteContext } from '../../../context/SiteContext';
import { ButtonContinue } from '../../common/Button/ButtonContinue';
import { ButtonCommand } from '../../common/Button/ButtonCommand';
import {
  InsuranceJourneyContext,
  InsuranceJourneyState,
  QuoteStatus
} from '../../common/CommonTypes';
import { CommonValidation } from '../../common/CommonValidation/commonValidation';
import LoadingContainer from '../../common/LoadingContainer/LoadingContainer';
import useCarInsuranceController from '../../../hooks/insuranceJourney/useCarInsuranceController';
import { useErrorBoundary } from 'react-error-boundary';
import PopupComponent from '../../common/PopupComponent';
import FairUseNoticePage from './FairUseNoticePage';

type PageErrors = {
  iAgree: string;
  keepingYouInformed: string;
  keepingYouInformedOptions: string;
  nextYearInsurance: string;
  yourDocuments: string;
};

const initialFormErrors: PageErrors = {
  iAgree: '',
  keepingYouInformed: '',
  keepingYouInformedOptions: '',
  nextYearInsurance: '',
  yourDocuments: ''
};

const ConfirmationDetailsPage = () => {
  const navigate = useNavigate();
  const { showBoundary } = useErrorBoundary();
  const [formErrors, setFormErrors] = useState<PageErrors>(initialFormErrors);
  const [loading, setLoading] = useState(false);
  const { journey, pageEvents } = useOutletContext<InsuranceJourneyContext>();
  const {
    confirmationDetails,
    staticData,
    insuranceNavigationStatus,
    savingQuote
  } = journey;

  const {
    iAgree,
    keepingYouInformed,
    nextYearInsurance,
    yourDocuments,
    marketingViaEmail,
    marketingViaPhone,
    marketingViaPost,
    marketingViaTextMessage
  } = confirmationDetails;

  const { confirmationDetailsPage, commonEvents } = pageEvents;
  const {
    onChangeIAgree,
    onChangeKeepingYouInformed,
    onChangeNextYearInsurance,
    onChangeYourDocuments,
    onChangeMarketingViaEmail,
    onChangeMarketingViaPhone,
    onChangeMarketingViaPost,
    onChangeMarketingViaTextMessage
  } = confirmationDetailsPage;

  const { setSavingQuote, onPageChange, setQuote } = commonEvents;
  const { saveQuote } = useCarInsuranceController();
  const [showPopupComponent, setShowPopupComponent] = useState(false);

  const validate = (): boolean => {
    let isValid = true;
    const updatedFormErrors = { ...initialFormErrors };
    if (!iAgree) {
      updatedFormErrors.iAgree = 'This field is required';
      isValid = false;
    }

    if (!site.user.isDealer) {
      if (CommonValidation.isEmpty(keepingYouInformed)) {
        updatedFormErrors.keepingYouInformed = 'This field is required';
        isValid = false;
      } else if (keepingYouInformed === 'Yes') {
        if (
          marketingViaEmail === false &&
          marketingViaPhone === false &&
          marketingViaPost === false &&
          marketingViaTextMessage === false
        ) {
          updatedFormErrors.keepingYouInformedOptions =
            'At least one option is required';
          isValid = false;
        }
      }

      if (CommonValidation.isEmpty(yourDocuments)) {
        updatedFormErrors.yourDocuments = 'This field is required';
        isValid = false;
      }
    }

    if (CommonValidation.isEmpty(nextYearInsurance)) {
      updatedFormErrors.nextYearInsurance = 'This field is required';
      isValid = false;
    }

    setFormErrors(updatedFormErrors);
    return isValid;
  };

  const { site } = useContext(SiteContext);

  const { brand } = site;

  const { brandDisplayFullName } = brand;

  useEffect(() => {
    window.scrollTo(0, 0);
    // Resetting the global state for field IAgree when component loads first time
    onChangeIAgree(false);
  }, []);

  const handleOnChangeIAgree = () => {
    onChangeIAgree(!iAgree);
  };

  const handleOnChangeMarketingViaEmail = () => {
    onChangeMarketingViaEmail(!marketingViaEmail);
  };

  const handleOnChangeMarketingViaPhone = () => {
    onChangeMarketingViaPhone(!marketingViaPhone);
  };

  const handleOnChangeMarketingViaPost = () => {
    onChangeMarketingViaPost(!marketingViaPost);
  };

  const handleOnChangeMarketingViaText = () => {
    onChangeMarketingViaTextMessage(!marketingViaTextMessage);
  };

  const onBackClick = () => {
    const path: string = setCountryCode(
      PageAbsolutePath.Insurance.AdditionalDriverDetailsPage,
      site.countryCode
    );
    navigate(path);
  };

  const saveFullQuoteSuccess = (quote: InsuranceJourneyState) => {
    console.log(quote);
    setQuote({
      ...quote,
      staticData: [...staticData],
      insuranceNavigationStatus: { ...insuranceNavigationStatus }
    });
    setLoading(false);
    redirectUser();
  };

  const redirectUser = () => {
    //removing as this logic was added for testing (keeping commented for the time being in case other logic from TIA needs to be applied)
    //if (
    //  drivingHistoryDetails.hadDrivingConvictions ||
    //  additionalDriverDetails.hadDrivingConvictions
    //) {
    //  const path: string = setCountryCode(
    //    PageAbsolutePath.Insurance.Referral,
    //    site.countryCode
    //  );
    //  navigate(path);
    //  onPageChange(PageAbsolutePath.Insurance.ConfirmationPage, true);
    //} else {
    const path: string = setCountryCode(
      PageAbsolutePath.Insurance.YourQuote,
      site.countryCode
    );
    navigate(path);
    onPageChange(PageAbsolutePath.Insurance.ConfirmationPage, true);
    //}
  };

  const onBackendFailure = (err: string) => {
    showBoundary(new Error(err));
  };

  const onNextClick = () => {
    const isValid = validate();

    if (!isValid) return;
    else {
      setLoading(true);
      saveQuote(
        { ...journey, quoteStatus: QuoteStatus.Full },
        site.user.token,
        saveFullQuoteSuccess,
        onBackendFailure
      );
    }
  };

  const renderFairUseNoticePage = () => {
    return <FairUseNoticePage isShowingInPopup />;
  };

  const onClosePopup = () => {
    setShowPopupComponent(false);
  };

  return (
    <LoadingContainer loading={loading} message={InsuranceLoadingMessage}>
      <div className='one-col'>
        <ButtonCommand
          name='btn-back-top'
          text={'< Back'}
          onClick={onBackClick}
        />
      </div>
      <h4 className='section-header m1-top'>Confirmation Page</h4>
      <div className='one-col'>
        <h5 className='control-label'>
          <b>{site.user.isDealer ? 'Dealer Confirmation' : 'Confirmation'}</b>
        </h5>
        <p>
          {site.user.isDealer
            ? `I confirm and understand that reasonable care has been used to 
          supply correct information provided by the customer; that the 
          customer/applicant has given their consent for information to be entered 
          on their behalf; that if the information is found to be incorrect that 
          the policy could be cancelled, or a claim rejected; and that the data 
          provided will be used as stated in the `
            : `I confirm that reasonable care has been used to supply correct
          information; I understand that if the information is found to be
          incorrect that the policy could be cancelled, or a claim rejected; and
          understand that the data provided will be used as stated in the `}
          <Link
            id='lnkFairUseNoticeInText'
            className='in-text inherited'
            onClick={() => setShowPopupComponent(true)}
            to=''>
            Fair use notice
          </Link>
          .
        </p>
      </div>
      <div className='one-col'>
        <Checkbox
          id={'chkConfirmationIAgree'}
          checked={iAgree}
          text={'I Agree'}
          onChange={handleOnChangeIAgree}
        />
        {formErrors.iAgree && (
          <div className='error-style'>{formErrors.iAgree}</div>
        )}
      </div>

      {!site.user.isDealer && (
        <>
          <div className='one-col'>
            <h5 className='control-label'>
              <b>Keeping you informed</b>
            </h5>
            <p>
              Every now and then we would like to contact you with news and
              special offers about {brandDisplayFullName} insurance and our
              related accident repair services. We limit the number of contacts
              you receive and do not sell or pass on any personal information to
              any third parties for their marketing activities. Please confirm
              whether you’re happy to receive updates.
            </p>
          </div>
          <div className='one-col'>
            {renderRadioOptions(
              ['Yes', 'No'],
              'keepingYouInformed',
              keepingYouInformed,
              onChangeKeepingYouInformed
            )}
            {formErrors.keepingYouInformed && (
              <div className='error-style'>{formErrors.keepingYouInformed}</div>
            )}
          </div>
          {keepingYouInformed === 'Yes' && (
            <div className='one-col modify-label-order'>
              <Checkbox
                id='chk-marketing-post'
                checked={marketingViaPost}
                text='Post'
                onChange={handleOnChangeMarketingViaPost}
              />
              <Checkbox
                id='chk-marketing-phone'
                checked={marketingViaPhone}
                text='Telephone'
                onChange={handleOnChangeMarketingViaPhone}
              />
              <Checkbox
                id='chk-marketing-email'
                checked={marketingViaEmail}
                text='Email (or other electronic means)'
                onChange={handleOnChangeMarketingViaEmail}
              />
              <Checkbox
                id='chk-marketing-text'
                checked={marketingViaTextMessage}
                text='Text message'
                onChange={handleOnChangeMarketingViaText}
              />
              {formErrors.keepingYouInformedOptions && (
                <div className='error-style'>
                  {formErrors.keepingYouInformedOptions}
                </div>
              )}
            </div>
          )}
        </>
      )}

      <div className='one-col'>
        <h5 className='control-label'>
          <b>Next year's insurance</b>
        </h5>
        <p>
          For your convenience and to ensure there’s no gap in your cover, we
          will securely store your payment details and enable continuous payment
          authority. We may use these details to automatically renew your policy
          next year.
        </p>
        <p>
          We’ll send you a renewal invitation at least 14 days before taking any
          payment. Please call us if your circumstances change or you do not
          wish us to automatically renew your policy.
        </p>
      </div>
      <div className='one-col'>
        {renderRadioOptions(
          ['Yes', 'No'],
          'nextYearInsurance',
          nextYearInsurance,
          onChangeNextYearInsurance
        )}
        {formErrors.nextYearInsurance && (
          <div className='error-style'>{formErrors.nextYearInsurance}</div>
        )}
      </div>

      {!site.user.isDealer && (
        <>
          <div className='one-col'>
            <h5 className='control-label'>
              <b>Your Documents</b>
            </h5>
            <p>Please confirm how you would like to receive your documents</p>
          </div>
          <div className='one-col'>
            {renderRadioOptions(
              ['Email', 'Post'],
              'yourDocuments',
              yourDocuments,
              onChangeYourDocuments
            )}
            {formErrors.yourDocuments && (
              <div className='error-style'>{formErrors.yourDocuments}</div>
            )}
          </div>
        </>
      )}

      <div className='even-space-out flex-row form-nav'>
        <ButtonBack
          name='btn-back'
          text='Back'
          onClick={onBackClick}
          additionalClassNames='btn-back'
        />
        <ButtonContinue
          name='btn-save-later'
          text='Save For Later'
          onClick={() => setSavingQuote(true)}
          additionalClassNames='btn-save'
          disabled={savingQuote}
        />
        <ButtonContinue
          name='btn-next'
          text='Next'
          onClick={onNextClick}
          additionalClassNames='btn-continue'
        />
        <PopupComponent
          showPopupComponent={showPopupComponent}
          renderComponent={renderFairUseNoticePage}
          onClosePopup={onClosePopup}
          popupTitle='Your Privacy'
        />
      </div>
    </LoadingContainer>
  );
};

export default ConfirmationDetailsPage;
